import { useState } from "react";
import { useGlobalQuery } from "../api/useGlobalQuery";
import { FIRST_CALCULATION_TEXT } from "../lib/definitions/analysis";
import {
  ADVERTISERS_DESC,
  ADVERTISERS_TITLE,
  CHANGE_TIP,
  changeLabel,
  changeText,
  isPending,
  NO_ROWS_TEXT,
  shareText,
  staleNote,
  TALKERS_DESC,
  TALKERS_TITLE,
  TALKERS_WINDOWS,
  totalsText,
  type TalkerBlock,
  type TalkersPayload,
  type TalkersResponse,
  type TalkersWindow,
} from "../lib/talkers";
import { formatLocalDateTime } from "../lib/format";
import { HoverTip } from "./HoverTip";
import { SectionCard } from "./SectionCard";

// Analysis > MeshCore > Top Talkers. The lists are stored by the background pass; this only reads them.

function TalkersTable({ block, noun, windowKey }: { block: TalkerBlock; noun: string; windowKey: TalkersWindow }) {
  if (block.rows.length === 0) return <div className="node-detail-note">{NO_ROWS_TEXT}</div>;
  return (
    <>
      <div className="settings-field-desc">{totalsText(block, noun)}</div>
      <div className="node-list-table-wrap">
        <table className="node-list-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Node</th>
              <th>Count</th>
              <th>Share</th>
              <th>
                <HoverTip text={CHANGE_TIP}>
                  <span>Change {changeLabel(windowKey)}</span>
                </HoverTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r) => (
              <tr key={r.node_id}>
                <td>{r.rank}</td>
                <td>
                  <a href={`#/nodes/${encodeURIComponent(r.node_id)}`} className="node-name-link">
                    {r.name}
                  </a>
                </td>
                <td>{r.count.toLocaleString("en-US")}</td>
                <td>{shareText(r.share)}</td>
                <td>{changeText(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function TalkersTab() {
  const [windowKey, setWindowKey] = useState<TalkersWindow>("24h");
  // Stored snapshot in the shared query cache: switching tabs or window never blanks data already there.
  const q = useGlobalQuery<TalkersResponse>(["talkers", "meshcore", windowKey], `/api/audit/talkers?window=${windowKey}`, 60000);
  const data = q.data;
  const payload: TalkersPayload | null = data && !isPending(data) ? data : null;

  return (
    <div className="talkers-tab">
      <div className="node-list-toolbar">
        <div className="theme-toggle" role="group" aria-label="Window">
          {TALKERS_WINDOWS.map((w) => (
            <button key={w} type="button" data-active={windowKey === w} onClick={() => setWindowKey(w)}>
              {w}
            </button>
          ))}
        </div>
      </div>
      {data === null && q.loading && <div className="empty-state">Loading…</div>}
      {data === null && !q.loading && q.error && <div className="node-detail-note">Not available right now.</div>}
      {data !== null && isPending(data) && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {payload && staleNote(payload, formatLocalDateTime) && <div className="node-detail-note">{staleNote(payload, formatLocalDateTime)}</div>}
      {payload && (
        <>
          <SectionCard title={TALKERS_TITLE} description={TALKERS_DESC}>
            <TalkersTable block={payload.talkers} noun="packets" windowKey={windowKey} />
          </SectionCard>
          <SectionCard title={ADVERTISERS_TITLE} description={ADVERTISERS_DESC}>
            <TalkersTable block={payload.advertisers} noun="adverts" windowKey={windowKey} />
          </SectionCard>
        </>
      )}
    </div>
  );
}
